/**
 * E002-T044: Status Tracking Section Component
 * S020-8.20.6: Column-Level Conflict Detection
 *
 * Allows users to configure post-processing status tracking.
 * After successful automation run, a Select column is updated to mark rows as processed.
 * Failed rows (when onFailure='continue') keep their original status.
 */

import { notify, Switch } from '@affine/component';
import { useI18n } from '@affine/i18n';
import { InformationFillDuotoneIcon, LockIcon } from '@blocksuite/icons/rc';
import type { FC } from 'react';
import { useCallback, useMemo, useState } from 'react';

import type {
  AutomationConfig,
  StatusTrackingConfig,
} from '../../services/automation';
import {
  DEFAULT_STATUS_TRACKING_CONFIG,
  STATUS_STANDARD_VALUES,
} from '../../services/automation';
import type { ColumnConflict } from '../../services/automation-discovery';
import type { ColumnInfo } from '../../services/database-discovery';
import { CollapsibleSection } from './collapsible-section';
import { SectionControlRow, SectionNotice } from './section-layout';
import * as styles from './status-tracking-section.css';

export const CREATE_STATUS_COLUMN_OPTION = '__create_status_column__';

interface StatusTrackingSectionProps {
  config: AutomationConfig;
  onUpdate: (updates: Partial<AutomationConfig>) => void;
  /** Available select columns from source table */
  selectColumns?: ColumnInfo[];
  /** S020-8.20.6: Map of column IDs to conflict info */
  usedColumns?: Map<string, ColumnConflict>;
  /** S020-8.20.6: Callback to navigate to conflicting automation */
  onNavigateToConflict?: (automationId: string, docId: string) => void;
  /** Create a status Select column in the primary input table */
  onCreateStatusColumn?: () => Promise<void> | void;
  /** Callback when the section info icon is clicked */
  onInfoClick?: () => void;
}

export const StatusTrackingSection: FC<StatusTrackingSectionProps> = ({
  config,
  onUpdate,
  selectColumns = [],
  usedColumns = new Map(),
  onNavigateToConflict,
  onCreateStatusColumn,
  onInfoClick,
}) => {
  const t = useI18n();
  const statusTracking =
    config.statusTracking ?? DEFAULT_STATUS_TRACKING_CONFIG;

  // S020-8.20.6: State for conflict dialog
  const [conflictColumn, setConflictColumn] = useState<ColumnConflict | null>(
    null
  );
  const [isCreatingStatusColumn, setIsCreatingStatusColumn] = useState(false);

  // Update status tracking config
  const updateStatusTracking = useCallback(
    (updates: Partial<StatusTrackingConfig>) => {
      onUpdate({
        statusTracking: {
          ...statusTracking,
          ...updates,
        },
      });
    },
    [onUpdate, statusTracking]
  );

  // Toggle enabled state
  // sl-optc: When disabling status tracking, also disable schedule (loop prevention)
  const handleToggle = useCallback(
    (checked: boolean) => {
      if (!checked && config.triggerConfig?.enabled) {
        // Disabling status tracking while schedule is active → disable schedule too
        notify({
          title: t['com.streamlain.streamlain.tracking.schedule-deactivated'](),
          message:
            t['com.streamlain.streamlain.tracking.schedule-deactivated-msg'](),
          style: 'normal',
          theme: 'warning',
        });
        onUpdate({
          statusTracking: { ...statusTracking, enabled: false },
          triggerConfig: { ...config.triggerConfig, enabled: false },
        });
      } else {
        updateStatusTracking({ enabled: checked });
      }
    },
    [updateStatusTracking, config.triggerConfig, onUpdate, statusTracking, t]
  );

  // Handle column selection
  const handleColumnSelect = useCallback(
    (columnId: string) => {
      updateStatusTracking({ columnId: columnId || null });
    },
    [updateStatusTracking]
  );

  // Handle success value change
  const handleSuccessValue = useCallback(
    (value: string) => {
      updateStatusTracking({ successValue: value });
    },
    [updateStatusTracking]
  );

  // Handle error value change
  const handleErrorValue = useCallback(
    (value: string) => {
      updateStatusTracking({ errorValue: value || undefined });
    },
    [updateStatusTracking]
  );

  // All success value options
  const successOptions = useMemo(() => [...STATUS_STANDARD_VALUES.success], []);

  // All error value options
  const errorOptions = useMemo(() => [...STATUS_STANDARD_VALUES.error], []);

  // Check if configuration is complete
  const isConfigured = useMemo(() => {
    if (!statusTracking.enabled) return false;
    return statusTracking.columnId !== null;
  }, [statusTracking.enabled, statusTracking.columnId]);

  // Badge based on status
  const badge = statusTracking.enabled
    ? {
        label: isConfigured
          ? t['com.streamlain.streamlain.tracking.badge-active']()
          : t['com.streamlain.streamlain.tracking.badge-incomplete'](),
        status: isConfigured ? 'active' : 'incomplete',
      }
    : undefined;

  // S020-8.20.6: Handle "Go to" conflict navigation
  const handleGoToConflict = useCallback(() => {
    if (conflictColumn && onNavigateToConflict) {
      onNavigateToConflict(
        conflictColumn.conflictingAutomationId,
        conflictColumn.conflictingDocId
      );
      setConflictColumn(null);
    }
  }, [conflictColumn, onNavigateToConflict]);

  const handleCreateStatusColumn = useCallback(async () => {
    if (!onCreateStatusColumn || isCreatingStatusColumn) return;
    setIsCreatingStatusColumn(true);
    try {
      await onCreateStatusColumn();
    } finally {
      setIsCreatingStatusColumn(false);
    }
  }, [isCreatingStatusColumn, onCreateStatusColumn]);

  return (
    <CollapsibleSection
      title={t['com.streamlain.streamlain.input.status-tracking']()}
      subtitle={t['com.streamlain.streamlain.tracking.mode-status-column']()}
      badge={badge as { label: string; status: 'active' | 'incomplete' }}
      defaultCollapsed
      onInfoClick={onInfoClick}
    >
      {/* Enable Toggle Row */}
      <SectionControlRow
        label={t['com.streamlain.streamlain.tracking.enable']()}
      >
        <Switch checked={statusTracking.enabled} onChange={handleToggle} />
      </SectionControlRow>

      {/* Configuration (only when enabled) */}
      {statusTracking.enabled && (
        <>
          {/* Status Column Configuration */}
          <>
            {/* Column Selection Row */}
            <SectionControlRow
              label={t['com.streamlain.streamlain.tracking.status-column']()}
              description={t[
                'com.streamlain.streamlain.tracking.select-column'
              ]()}
            >
              <select
                className={styles.select}
                value={statusTracking.columnId || ''}
                disabled={isCreatingStatusColumn}
                onChange={e => {
                  const columnId = e.target.value;
                  if (columnId === CREATE_STATUS_COLUMN_OPTION) {
                    void handleCreateStatusColumn();
                    return;
                  }
                  // S020-8.20.6: Check for conflicts before selecting
                  const conflict = usedColumns.get(columnId);
                  if (conflict) {
                    setConflictColumn(conflict);
                    // Reset selection to previous value
                    e.target.value = statusTracking.columnId || '';
                  } else {
                    handleColumnSelect(columnId);
                  }
                }}
              >
                <option value="">
                  {t['com.streamlain.streamlain.common.select']()}
                </option>
                {selectColumns.map(col => {
                  const conflict = usedColumns.get(col.id);
                  return (
                    <option
                      key={col.id}
                      value={col.id}
                      className={conflict ? styles.lockedOption : undefined}
                    >
                      {conflict ? `${col.name} (belegt)` : col.name}
                    </option>
                  );
                })}
                {onCreateStatusColumn && (
                  <option value={CREATE_STATUS_COLUMN_OPTION}>
                    {t['com.streamlain.gantt.wizard.createNewColumn']()}
                  </option>
                )}
              </select>
            </SectionControlRow>

            {/* S020-8.20.6: Column Conflict Dialog */}
            {conflictColumn && (
              <div className={styles.conflictBox}>
                <div className={styles.conflictHeader}>
                  <LockIcon className={styles.conflictIcon} />
                  <strong>
                    {t['com.streamlain.streamlain.tracking.column-in-use']()}
                  </strong>
                </div>
                <div className={styles.conflictText}>
                  {t['com.streamlain.streamlain.tracking.conflict-text']()}
                </div>
                <div className={styles.conflictActions}>
                  <button
                    className={styles.conflictButton}
                    onClick={handleGoToConflict}
                  >
                    {`${t['com.streamlain.streamlain.tracking.conflict-goto']()} "${conflictColumn.conflictingAutomationName}" ↗`}
                  </button>
                  <button
                    className={styles.conflictButtonSecondary}
                    onClick={() => setConflictColumn(null)}
                  >
                    {t['com.streamlain.streamlain.tracking.conflict-choose']()}
                  </button>
                </div>
              </div>
            )}

            {/* No columns warning */}
            {selectColumns.length === 0 && (
              <SectionNotice
                variant="warning"
                icon={<InformationFillDuotoneIcon />}
              >
                <span>
                  {t['com.streamlain.streamlain.tracking.no-columns']()}
                </span>
              </SectionNotice>
            )}

            {/* Status Values (only when column selected) */}
            {statusTracking.columnId && (
              <div className={styles.valuesGrid}>
                {/* Success Value */}
                <div className={styles.valueField}>
                  <label className={styles.valueLabel}>
                    <span className={styles.successDot} />
                    {t['com.streamlain.streamlain.tracking.on-success']()}
                  </label>
                  <select
                    className={styles.valueSelect}
                    value={statusTracking.successValue}
                    onChange={e => handleSuccessValue(e.target.value)}
                  >
                    {successOptions.map(opt => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Error Value */}
                <div className={styles.valueField}>
                  <label className={styles.valueLabel}>
                    <span className={styles.errorDot} />
                    {t['com.streamlain.streamlain.tracking.on-error']()}
                  </label>
                  <select
                    className={styles.valueSelect}
                    value={statusTracking.errorValue || ''}
                    onChange={e => handleErrorValue(e.target.value)}
                  >
                    <option value="">
                      {t['com.streamlain.streamlain.common.none']()}
                    </option>
                    {errorOptions.map(opt => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {/* Info Box (only when fully configured) */}
            {statusTracking.columnId && (
              <SectionNotice icon={<InformationFillDuotoneIcon />}>
                <span>
                  <strong>
                    {t['com.streamlain.streamlain.tracking.system-filter']()}
                  </strong>{' '}
                  {t['com.streamlain.streamlain.tracking.skip-info']({
                    value: statusTracking.successValue,
                  })}
                </span>
              </SectionNotice>
            )}
          </>
        </>
      )}
    </CollapsibleSection>
  );
};
